import { useState } from "react";
import { toast } from "@volca/ui/lib/toast";
import { useUpdateQuickbooksConnection } from "@/business/quickbooks-query";
import type { QuickbooksBillSettings } from "@/business/quickbooks-utils";
import { getErrorMessage } from "@/core/api/errors";

type Draft = Partial<QuickbooksBillSettings>;

/**
 * Unsaved bill settings and their save. Held by the AP page rather than the
 * QuickBooks view, so switching views keeps a draft and a save in flight
 * still reports its outcome.
 *
 * Drafts and errors are kept per business: the page outlives a business
 * switch, and one business's unsaved date must never be saved to another.
 */
export const useBillSettingsDraft = (businessId: string | null) => {
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [savingFor, setSavingFor] = useState<Record<string, true>>({});
  const update = useUpdateQuickbooksConnection();
  const key = businessId ?? "";

  const setError = (id: string, message: string | null) =>
    setErrors(({ [id]: _, ...rest }) =>
      message === null ? rest : { ...rest, [id]: message },
    );

  const change = (changes: Draft) => {
    setError(key, null);
    setDrafts((prev) => ({ ...prev, [key]: { ...prev[key], ...changes } }));
  };

  const setSaving = (id: string, saving: boolean) =>
    setSavingFor(({ [id]: _, ...rest }) =>
      saving ? { ...rest, [id]: true } : rest,
    );

  // Each save settles through its own promise: a later save (say for another
  // business) replaces the mutation's per-call callbacks, but not this.
  const save = (values: QuickbooksBillSettings) => {
    if (!businessId) return;
    const savedFor = businessId;
    setSaving(savedFor, true);
    update
      .mutateAsync({ businessId: savedFor, changes: values })
      .then(() => {
        setDrafts(({ [savedFor]: _, ...rest }) => rest);
        toast.success("QuickBooks bill settings saved");
      })
      // A 422 explains itself ("needs an account and a start date"), so it
      // stays beside the fields rather than in a passing toast.
      .catch((err: unknown) => setError(savedFor, getErrorMessage(err)))
      .finally(() => setSaving(savedFor, false));
  };

  return {
    draft: drafts[key] ?? {},
    change,
    error: errors[key] ?? null,
    save,
    isSaving: !!savingFor[key],
  };
};

export type BillSettingsDraft = ReturnType<typeof useBillSettingsDraft>;
